import { useLoaderData } from 'react-router-dom';
import { Head } from 'vite-react-ssg';

// Services
import { getFooter, type FooterType } from '@/services/footer/footerService';
import { getHeader, type HeaderType } from '@/services/header/headerService';
import { getOffresEtServices, type OffreEtServicesType } from '@/services/offres-et-services/offresStServicesService';

// Components
import Header from '@/components/organisms/Header/Header';
import Footer from '@/components/organisms/Footer/Footer';
import { PageBlocks } from '@/components/blocks/Blocks';

// Utils
import { getLocale } from '@/utils/localeSlug';
import { buildSEOUrl, getAlternateLocalizations } from '@/utils/seo';
import { withTrailingSlashIfInternal } from '@/utils/trailingSlash';

// Styles
import styles from './OffresEtServices.module.css';

export async function offresEtServicesLoader({ request }: { request: Request }) {
  const url = new URL(request.url);

  const locale = getLocale(url);

  const [header, footer, page] = await Promise.all([
    getHeader(locale),
    getFooter(locale),
    getOffresEtServices(locale),
  ]);

  return {
    header,
    footer,
    page,
  };
}


function OffresEtServices() {
  // API Calls
  const data = useLoaderData() as Awaited<ReturnType<typeof offresEtServicesLoader>>;
  const baseUrl = import.meta.env.VITE_DOMAIN;

  if (!data) return null;

  const { header, footer, page } = data;

  if (!page) return null;

  return (
    <div>
      <Head htmlAttributes={{ lang: page.locale }}>
        <title>{page.seo.metaTitle}</title>
        <meta name="description" content={page.seo.metaDescription} />
        <link
          rel='canonical'
          href={page.seo.canonicalLink
            ? withTrailingSlashIfInternal(page.seo.canonicalLink, baseUrl)
            : buildSEOUrl(baseUrl, page.locale, page.slug)}
        />
        <link
          rel='alternate' hrefLang={page.locale}
          href={buildSEOUrl(baseUrl, page.locale, page.slug)}
        />
        {
          getAlternateLocalizations(page.localizations, { requireSlug: true }).map((localization) => (
            <link
              key={localization.documentId}
              rel="alternate"
              hrefLang={localization.locale}
              href={buildSEOUrl(baseUrl, localization.locale, localization.slug)}
            />
          ))
        }
        {
          page.locale === 'fr-FR' ? (
            <link
              rel="alternate"
              hrefLang="x-default"
              href={buildSEOUrl(baseUrl, page.locale, page.slug)}
            />
          ) : (
            (() => {
              const fr = page.localizations.find(loc => loc.locale === 'fr-FR');
              if (fr) {
                return (
                  <link
                    rel="alternate"
                    hrefLang="x-default"
                    href={buildSEOUrl(baseUrl, fr.locale, fr.slug)}
                  />
                );
              }
              return null;
            })()
          )
        }
      </Head>

      <Header
        color='dark'
        header={header as HeaderType}
        localizations={page.localizations}
      />
      <div className={styles.body}>
        <PageBlocks blocks={(page as OffreEtServicesType).blocks} />
      </div>

      <Footer
        footer={footer as FooterType}
      />
    </div>
  );
}

export default OffresEtServices;